import React, { useEffect, useRef, useState } from 'react';
import { PhoneCall, Clock } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
import { SUPPORT_MAILTO, SUPPORT_EMAIL } from '../lib/links';

/*
 * TALK TO SALES.
 *
 * This page used to embed a Calendly scheduler. It no longer does, and the
 * reason matters: a calendar asks for a slot before it asks anything about the
 * person, so the lead lands in a calendar rather than in the CRM, and sales
 * arrives at the call knowing nothing. The Close web form captures who they
 * are and what they need first; booking happens in the follow-up, which is
 * also where the SMS consent recorded on the form becomes usable.
 *
 * The form is Close's own hosted web component — its markup, validation,
 * multi-step behaviour and the STOP/HELP consent language all live on Close's
 * side, NOT here. Do not rebuild these fields locally: the consent wording on
 * that form is what the site's Terms and Privacy SMS sections were written to
 * match, and a hand-rolled copy would drift out of sync with the A2P
 * registration.
 */
const CLOSE_SCRIPT = 'https://webforms.closeiocdn.com/webforms.js';
const CLOSE_FORM_ID = 'form_034KqJTBGwSfdOts2QbCkL';

/*
 * TWO ROUTES IN, NOT AN AGENDA.
 *
 * A checklist of what happens on the call was tried here and put back: it read
 * as terms rather than as an offer, and the three lines all said "here is what
 * we will do to you". These two say what the reader can choose between — see
 * it, or start using it — which is the actual decision at this point.
 */
const PROMISES = [{
  icon: PhoneCall,
  title: 'Personal demo.',
  body: 'See Giga running on the tools your team already uses, and what it could take off their hands.'
}, {
  icon: Clock,
  title: 'Set up your trial.',
  body: 'Get your workspace connected and a first agent running, with us on the call.'
}];

/*
 * THE FIGURE THAT CLOSES THE COLUMN.
 *
 * The left column ended on two short bullets and then stopped, leaving a tall
 * empty run beside a form that keeps going. This is the same cut-out figure
 * the site closes on elsewhere, so the page ends on a person rather than on
 * nothing — and it is a photograph, not a decorative shape, which is the only
 * kind of filler worth having.
 *
 * It is cropped by the column's bottom edge on purpose: a figure that fits
 * entirely inside its box reads as a sticker, one that runs out of frame reads
 * as standing behind the page.
 */
const FIGURE = 'https://cdn.magicpatterns.com/uploads/32qVG7t7z33R94gQev93dK/download_(52)_8.png';
export function BookDemo() {
  useScreenInit();
  const holder = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);

  /*
   * ONE ARRIVAL, NOT THREE.
   *
   * Left alone, this page assembles itself in front of the reader: Close's
   * script arrives, its element goes from an empty box to a full form, the
   * card grows, the grid row grows with it, and the figure — pinned to the
   * bottom of that row — slides down. Three visible movements for something
   * that should simply be there.
   *
   * So both are held at opacity 0 until the form has stopped resizing AND the
   * figure has decoded, then revealed together. The space is reserved up
   * front, so nothing below moves either. Waiting is not the cost people
   * think it is — a still page that appears 200ms later reads as faster than
   * one that visibly builds itself.
   */
  const [formSettled, setFormSettled] = useState(false);
  const [figureLoaded, setFigureLoaded] = useState(false);
  const ready = formSettled && figureLoaded;

  // Settled = the holder's height has not changed for 250ms. A simple
  // "script loaded" check fires too early: the element is defined well before
  // it has fetched its config and laid out its fields.
  useEffect(() => {
    const node = holder.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      setFormSettled(true);
      return;
    }
    let quiet: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(quiet);
      quiet = setTimeout(() => setFormSettled(true), 250);
    });
    observer.observe(node);
    // Hard cap: if the widget never reports a size, reveal anyway rather
    // than leaving the page blank.
    const cap = setTimeout(() => setFormSettled(true), 4000);
    return () => {
      observer.disconnect();
      clearTimeout(quiet);
      clearTimeout(cap);
    };
  }, []);

  // Decoded before it is shown, so the figure cannot paint in halfway
  // through the fade.
  useEffect(() => {
    const img = new Image();
    img.onload = () => setFigureLoaded(true);
    img.onerror = () => setFigureLoaded(true);
    img.src = FIGURE;
  }, []);
  useEffect(() => {
    // Loaded at runtime rather than bundled, so a blocked or slow script leaves
    // the page intact instead of failing the build. Close ships an ES module,
    // hence type="module" and the CORS attribute — a plain classic script tag
    // silently does nothing.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CLOSE_SCRIPT}"]`);
    if (existing) return;
    const script = document.createElement('script');
    script.src = CLOSE_SCRIPT;
    script.type = 'module';
    script.crossOrigin = 'anonymous';
    script.defer = true;
    script.addEventListener('error', () => setFailed(true));
    document.body.appendChild(script);
  }, []);

  // Close's element is a custom element, not a React component. createElement
  // keeps TypeScript out of an argument about intrinsic JSX tags it has never
  // heard of.
  const closeForm = React.createElement('close-form', {
    id: CLOSE_FORM_ID
  });
  return <div className="min-h-screen w-full bg-[#FAF8F4]" data-id="element-7416" data-mp="3sefub7cmzde8">
      {/*
       * WIDER THAN THE SITE CONTAINER, DELIBERATELY.
       *
       * Marketing pages run at max-w-6xl (1152px) because that keeps a single
       * column of prose at a readable measure. This page is not prose — it is
       * two independent panels, and 1152px was splitting into a ~500px column
       * and a ~600px form with almost no margin either side, which is what
       * made it feel boxed in.
       *
       * At 1360px the extra 200px goes where it is useful: the form panel
       * grows (Close's two-up name fields stop feeling cramped) and the gutter
       * between the columns opens up. The copy does NOT stretch — every text
       * block below keeps its own max-w in ch, so the measure is unchanged.
       */}
      {/*
       * 1320px container, 32px gutters, 48px column gap.
       *
       * These three numbers exist to make ONE thing true: two genuinely equal
       * columns that are each at least 600px wide, which is the width below
       * which Close's form stops rendering properly.
       *   1320 − 64 (page gutters) − 48 (column gap) = 1208 / 2 = 604 each.
       *
       * Earlier versions used minmax() floors on the right track to guarantee
       * the 600px. That is what kept pushing the split past 50/50: a floor
       * only ever wins by taking width from the other column. Sizing the
       * container so 50% is ALREADY ≥ 600px means the columns can just be
       * 1fr 1fr and stay even at every width.
       */}
      <div className="relative mx-auto max-w-[1320px] px-6 pb-24 pt-32 md:px-8 md:pt-36 lg:pb-0" data-id="element-7417" data-mp="334fp2k5d0d1l">
        {/* Held until the whole page is ready, so the copy arrives with the
         form and the figure rather than sitting alone waiting for them.
         A page that appears complete 300ms later reads as faster than one
         that assembles itself in three stages. */}
        <div aria-busy={!ready} className={`transition-opacity duration-300 ease-out ${ready ? 'opacity-100' : 'opacity-0'}`} data-id="element-7418" data-mp="3lxjkob250zyu">
        {/* The row's height is reserved up front. Without it the row is as
         tall as whatever the form currently is, so the row — and the figure
         standing at the bottom of it — grows as the widget fills in. */}
        <div className="grid gap-12 lg:min-h-[860px] lg:grid-cols-2 lg:gap-12" data-id="element-7419" data-mp="3oizyeosyeja">

          {/* ── The case for spending the thirty minutes ─────────────────── */}
          {/* `isolate` scopes the -z-10 on the figure below to this column.
           Without it, a negative layer paints beneath the page's cream
           background as well and the figure vanishes entirely. */}
          <div className="relative isolate flex flex-col" data-id="element-7420" data-mp="u1q2shq9ywoz">
            <h1 className="font-title text-[40px] leading-[1.06] tracking-[-0.03em] text-[#2B211A] md:text-[52px]" data-id="element-7421" data-mp="6akssj70myml">
              Talk to one of our experts.
            </h1>
            <p className="mt-4 max-w-[42ch] text-[17px] leading-relaxed text-[#6B5D52]" data-id="element-7422" data-mp="g0vv61b1wzrd">
              Fill out the form and our team will get back to you shortly.
            </p>

            <ul className="mt-9 space-y-6" data-id="element-7423" data-mp="b8m6eoloa5qo">
              {PROMISES.map(({
                icon: Icon,
                title,
                body
              }) => <li key={title} className="flex gap-4" data-id="element-7424" data-mp="3eq4g71l09mtq">
                  <span className="mt-0.5 flex size-9 flex-none items-center justify-center rounded-full bg-[#FDF0E6]" data-id="element-7425" data-mp="1u93mj9sarwn2">
                    <Icon className="size-[17px] text-[#EA580C]" strokeWidth={2} data-id="element-7426" data-mp="1z0db07240txd" />
                  </span>
                  <div data-id="element-7427" data-mp="1l985wpfz39fn">
                    <p className="font-title text-[16px] font-semibold text-[#2B211A]" data-id="element-7428" data-mp="1l9q8s4x54lwj">
                      {title}
                    </p>
                    <p className="mt-1 max-w-[42ch] text-[14.5px] leading-relaxed text-[#6B5D52]" data-id="element-7429" data-mp="1p7o47phi848d">
                      {body}
                    </p>
                  </div>
                </li>)}
            </ul>

            {/* The lower-commitment way through, now a line rather than a
             panel. Boxed and bordered, it carried the same weight as the two
             promises above it — which made a footnote look like a third
             option. Set as quiet running text, it is there for whoever is
             looking for it and invisible to everyone else. */}
            <p className="mt-8 text-[13.5px] leading-relaxed text-[#8A7B6D]" data-id="element-7430" data-mp="xk5s5sogm0qf">
              Just have a question?{' '}
              <a href={SUPPORT_MAILTO} className="text-[#6B5D52] underline decoration-[#D9CFBE] underline-offset-4 transition-colors duration-150 ease-out hover:text-[#EA580C]" data-id="element-7431" data-mp="2nv7t5mixtumz">
                {SUPPORT_EMAIL}
              </a>{' '}
              reaches a person.
            </p>

            {/* ABSOLUTE, so the figure has no say in the layout.
             It is decoration: in the flow it was adding its own height and
             width to the column, which is one of the things squeezing the
             form. Taken out of the flow it sits at the bottom-left of the
             copy column, bleeds into the page margin, and stands on the
             footer's top edge without affecting either column's size.
              Hidden below lg: on a phone the copy runs straight into the form,
             so there is no empty run for it to fill. */}
            {/* -z-10 is the fix for it covering the copy: an absolutely
             positioned element paints ABOVE static siblings regardless of
             source order, so it needs an explicit negative layer to sit
             behind the text. At 50% it reads as a watermark rather than an
             illustration competing with the words. */}
            <img src={FIGURE} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-0 left-1/2 -z-10 hidden h-auto w-[648px] max-w-none -translate-x-1/2 select-none lg:block xl:w-[748px]" data-id="element-7432" data-mp="chg4ri9xgkdj" />
          </div>

          {/* ── The form ─────────────────────────────────────────────────── */}
          {/* The page has no bottom padding at lg so the figure can stand on
           the footer. That would drag the form card down onto it too, so the
           form column carries the clearance itself. */}
          <div className="relative z-10 lg:pb-24" data-id="element-7433" data-mp="31xub3amb935d">
            {/* 24px all the way up rather than growing to 40px at lg. The
             card's padding is part of the column's width floor, so every
             extra pixel of padding is a pixel the right column takes from
             the left one — which is what pushed the split past 50/50. */}
            <div className="w-full rounded-[22px] border border-[#EFE7DA] bg-white p-6 shadow-[0_1px_2px_rgba(43,33,26,0.05),0_18px_44px_-28px_rgba(43,33,26,0.25)]" data-id="element-7434" data-mp="15xbohmupio9v">
            {/* No min-width here. The 600px floor belongs on the grid track,
             which is declared at 680px so that 600px survives the card's
             40px padding on each side. A floor on the holder itself would be
             600px INSIDE the padding, pushing the card 80px past its column
             — which is the overflow. */}
            {/* The reserved box stays at full height whether or not the form
             has arrived, so the card never grows under the reader. Only its
             contents fade in. */}
            <div className="min-h-[680px]" data-id="element-7435" data-mp="23gwnh2nokb6n">
              <div ref={holder} className="close-form-holder w-full" data-id="element-7436" data-mp="27jt9z4gccrfv">
                {closeForm}
              </div>
            </div>
            {failed && <p className="mt-4 text-[14px] leading-relaxed text-[#6B5D52]" data-id="element-7437" data-mp="3s3ybc3lq3vhy">
                The form could not load. Email{' '}
                <a href={SUPPORT_MAILTO} className="font-semibold text-[#EA580C] underline underline-offset-4" data-id="element-7438" data-mp="3j1p4potksm9u">
                  {SUPPORT_EMAIL}
                </a>{' '}
                and we will pick it up from there.
              </p>}
            </div>
          </div>
        </div>
        </div>

        {/* Quiet mark of progress while the page is held. It sits where the
         content will be, so the eye does not have to travel when the page
         arrives, and it is unmounted the moment it is no longer true. */}
        {!ready && <div className="pointer-events-none absolute inset-x-0 top-[40vh] flex justify-center" role="status" aria-label="Loading" data-id="element-7439" data-mp="vj5zoewjtpt">
            <span className="block size-6 animate-spin rounded-full border-2 border-[#E7E0D4] border-t-[#EA580C]" data-id="element-7440" data-mp="ydt0t3aiq6uy" />
          </div>}
      </div>
    </div>;
}